import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Drawer,
  toast,
  Button,
  BarsLoader,
} from '@insight-health-ai/roseate/components';
import { ToolProps } from '.';
import {
  CameraOutlined,
  InboxOutlined,
  FileImageOutlined,
  EyeOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { Upload, UploadFile } from 'antd';
import { createPatientDocument, getPatientDocument } from 'src/api';
import { PatientDocument } from 'src/models/patientDocument';
import { UploadChangeParam } from 'antd/es/upload';
import classNames from 'classnames';
import { ErrorScreen, ScreenLoader } from 'src/components';
import { useScreenSize } from '@insight-health-ai/roseate/utils';

type AcceptedFileCategory = 'image' | 'document';

const acceptedFileCategoriesToExtensionsMap: Record<
  AcceptedFileCategory,
  string[]
> = {
  image: ['image/*'],
  document: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
};

type FileUploaderProps = ToolProps<{
  accepted_file_types: string; // comma separated list of accepted file categories
  allow_multiple: boolean;
  max_file_size?: number;
}>;

type DocumentProps = {
  documentId: string;
  minimized: boolean;
  uploadInProgress?: boolean;
  uploadFailed?: boolean;
};

const Document = ({
  documentId,
  minimized,
  uploadInProgress,
  uploadFailed,
}: DocumentProps) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [patientDocument, setPatientDocument] =
    useState<PatientDocument | null>(null);
  useEffect(() => {
    if (!documentId) {
      return;
    }
    getPatientDocument({ patient_document_id: documentId })
      .then((document) => {
        setPatientDocument(document);
      })
      .catch((error) => {
        setError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [documentId]);

  const renderDocument = () => {
    return (
      <img
        className={classNames('object-contain w-full', {
          'max-h-[320px]': minimized,
        })}
        src={patientDocument?.presigned_url ?? ''}
        alt={patientDocument?.name ?? ''}
      />
    );
  };

  if (loading || uploadInProgress) {
    return (
      <div className="h-[320px] max-w-full">
        <ScreenLoader
          loaderText={
            uploadInProgress ? 'Uploading document' : 'Loading preview'
          }
        />
      </div>
    );
  }
  if (error || uploadFailed) {
    return (
      <div className="h-[320px] max-w-full p-4">
        <ErrorScreen
          title={
            uploadFailed ? 'Error uploading document' : 'Error loading document'
          }
          description="Something went wrong!"
        />
      </div>
    );
  }

  return renderDocument();
};

export const FileUploader = ({
  args,
  onComplete,
  response,
}: FileUploaderProps) => {
  const [uploadState, setUploadState] = useState<
    Record<
      UploadFile['uid'],
      {
        file: UploadFile;
        error?: Error | null;
        loading?: boolean;
        documentId?: string;
      }
    >
  >({});
  const uploadingDocuments = useMemo(() => {
    return Object.values(uploadState).some((u) => u.loading);
  }, [uploadState]);
  const { mediaMax } = useScreenSize();
  const isDesktop = !mediaMax('md');
  const hiddenCameraUploadRef = useRef<HTMLButtonElement>(null);
  const hiddenGalleryUploadRef = useRef<HTMLButtonElement>(null);
  const [showUploadOptionDrawer, setShowUploadOptionDrawer] = useState(false);
  const [showPreviewDrawer, setShowPreviewDrawer] = useState(false);

  const {
    accepted_file_types = '',
    allow_multiple = true,
    max_file_size = 5,
  } = args || {};

  const maxFileSizeInBytes = max_file_size * 1024 * 1024;

  const acceptedFileExtensions = useMemo(() => {
    return accepted_file_types
      .split(',')
      .map((category) => {
        return acceptedFileCategoriesToExtensionsMap[
          category as AcceptedFileCategory
        ];
      })
      .flat();
  }, [accepted_file_types]);

  const validateFile = (file: UploadFile) => {
    // Prevent upload if a file with the same name and size already exists in the uploadState
    const fileExists = Object.values(uploadState).some(
      (u) => u.file.name === file.name && u.file.size === file.size
    );
    if (fileExists) {
      toast.error(`Detected duplicate file ${file.name}`);
      return false;
    }
    if (file.size! > maxFileSizeInBytes) {
      toast.error(`File size must be less than ${max_file_size}MB`);
      return false;
    }
    return true;
  };

  const handleFileUpload = async (file: UploadFile) => {
    setUploadState((prev) => ({
      ...prev,
      [file.uid]: { file, loading: true, documentId: undefined },
    }));
    try {
      const document = await createPatientDocument({
        source: 'vca',
        type: 'file',
        file: file as unknown as File,
      });
      setUploadState((prev) => ({
        ...prev,
        [file.uid]: {
          file,
          error: null,
          loading: false,
          documentId: document.patient_document_id,
        },
      }));
      return document;
    } catch (error) {
      setUploadState((prev) => ({
        ...prev,
        [file.uid]: {
          file,
          error: error as Error,
          loading: false,
          documentId: undefined,
        },
      }));
    }
  };

  const filesUploadingRef = useRef<Promise<PatientDocument | undefined>[]>([]);
  const handleOnChange = async ({
    file,
    fileList,
  }: UploadChangeParam<UploadFile>) => {
    if (validateFile(file)) {
      filesUploadingRef.current.push(handleFileUpload(file));
    } else {
      filesUploadingRef.current.push(Promise.resolve(undefined));
    }
    if (filesUploadingRef.current.length === fileList.length) {
      const documents = await Promise.all(filesUploadingRef.current);
      const documentIds = documents
        .map((d) => d?.patient_document_id!)
        .filter(Boolean) as string[];
      if (documentIds.length) {
        await onComplete({
          content: documentIds,
          content_type: 'patient_document_ids',
          args,
        });
      } else {
        toast.error('No documents were uploaded. Please try again.');
        return;
      }
    }
  };

  const documents: Omit<DocumentProps, 'minimized'>[] = useMemo(() => {
    if (response) {
      return Object.values(
        ((response.content as string[]) || []).filter(Boolean)
      ).map((u) => ({
        documentId: u,
        uploadInProgress: false,
        uploadFailed: false,
      }));
    }
    return Object.values(uploadState).map((u) => ({
      documentId: u.documentId || '',
      uploadInProgress: u.loading,
      uploadFailed: !!u.error,
    }));
  }, [response, uploadState]);

  const documentInChat = documents.slice(0, 1);

  if (documents.length) {
    return (
      <div className="shadow rounded-lg overflow-hidden">
        <div
          className="flex flex-col"
          onClick={() => {
            setShowPreviewDrawer(true);
          }}
        >
          {documentInChat.map((document) => (
            <Document
              key={document.documentId}
              documentId={document.documentId}
              uploadInProgress={document.uploadInProgress}
              uploadFailed={document.uploadFailed}
              minimized
            />
          ))}
          {documents.length > documentInChat.length && (
            <div className="flex justify-center items-center bg-gray-100 py-2">
              <Button
                variant="text"
                appearance="secondary"
                icon={
                  uploadingDocuments ? (
                    <BarsLoader size="sm" />
                  ) : (
                    <EyeOutlined />
                  )
                }
              >
                {uploadingDocuments
                  ? 'Uploading documents'
                  : `Show all documents (${documents.length})`}
              </Button>
            </div>
          )}
        </div>
        {showPreviewDrawer && (
          <Drawer
            padding="sm"
            open={showPreviewDrawer}
            onClose={() => {
              setShowPreviewDrawer(false);
            }}
            {...(mediaMax('sm')
              ? {
                  height: 'auto',
                  placement: 'bottom',
                  className: 'max-h-[95vh]',
                }
              : { width: 'min(75%, 720px)', placement: 'right' })}
            title={`Preview documents (${documents.length})`}
          >
            <div className="flex flex-col gap-4">
              {documents.map((document) => (
                <Document
                  key={document.documentId}
                  documentId={document.documentId}
                  uploadInProgress={document.uploadInProgress}
                  uploadFailed={document.uploadFailed}
                  minimized={false}
                />
              ))}
            </div>
          </Drawer>
        )}
      </div>
    );
  }

  const draggerProps = {
    accept: acceptedFileExtensions.join(','),
    fileList: [],
    multiple: allow_multiple,
    name: 'files',
    disabled: uploadingDocuments,
    beforeUpload: () => {
      return false;
    },
    onChange: handleOnChange,
  };

  return (
    <>
      <div
        className="shadow rounded-lg"
        onClick={() => {
          if (isDesktop) {
            return;
          }
          setShowUploadOptionDrawer(true);
        }}
      >
        {!isDesktop && (
          <Upload className="hidden" {...draggerProps} capture>
            <button type="button" ref={hiddenCameraUploadRef} />
          </Upload>
        )}
        <Upload.Dragger
          className="shadow rounded-lg"
          {...draggerProps}
          openFileDialogOnClick={isDesktop || showUploadOptionDrawer}
        >
          <button
            ref={hiddenGalleryUploadRef}
            type="button"
            className="hidden"
          />
          <p className="ant-upload-drag-icon">
            {uploadingDocuments ? <LoadingOutlined /> : <InboxOutlined />}
          </p>
          <p className="ant-upload-text text-primary">
            Click or drag file to this area to upload
            <p className="text-xs text-gray-500">txt, doc, docx, pdf</p>
          </p>
        </Upload.Dragger>
      </div>
      {showUploadOptionDrawer && (
        <Drawer
          open
          title="Select source"
          onClose={() => {
            setShowUploadOptionDrawer(false);
          }}
          placement="bottom"
          height="auto"
        >
          <div className="flex gap-4 justify-start">
            <div
              className="flex w-[88px] flex-col gap-2 items-center justify-center cursor-pointer text-gray-700 border rounded-lg p-4"
              onClick={() => {
                hiddenCameraUploadRef.current?.click();
              }}
            >
              <CameraOutlined className="text-2xl" />
              <p className="">Camera</p>
            </div>
            <div
              className="flex w-[88px] flex-col gap-2 items-center justify-center cursor-pointer text-gray-700 border rounded-lg p-4"
              onClick={() => {
                hiddenGalleryUploadRef.current?.click();
              }}
            >
              <FileImageOutlined className="text-2xl" />
              <p className="">Gallery</p>
            </div>
          </div>
        </Drawer>
      )}
    </>
  );
};
